import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import OrderAttachments from '../components/OrderAttachments';
import PageHeader from '../components/common/PageHeader';
import { createStructuredNote, parseNotesBlob } from '../utils/noteUtils';
import NoteCard from '../components/notes/NoteCard';

const AttachArtwork = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getToken, user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [designNotes, setDesignNotes] = useState('');
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [tempAttachments, setTempAttachments] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        
        const response = await fetch(`${API_URL}/orders/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch order');
        }

        const data = await response.json();
        const orderData = data.data || data;
        
        setOrder(orderData);
        setDesignNotes(orderData.designNotes || '');
        
      } catch (error) {
        console.error('Error fetching order:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load order details',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId, getToken]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = await getToken();

      const userName = user?.name || user?.email || 'User';

      // Prepare the note content - use the same "(date) User from Source: content"
      // structured format used everywhere else so this note parses and displays
      // consistently via the shared parseNotesBlob()/NoteCard system.
      let notesToSave = order.designNotes || '';

      // Only add new note if user entered something
      if (designNotes && designNotes.trim() !== (order.designNotes || '').trim()) {
        const newNote = createStructuredNote(designNotes.trim(), userName, 'artwork', 'general');
        notesToSave = notesToSave
          ? `${notesToSave}\n\n${newNote}`
          : newNote;
      }

      // Update order with design notes
      const response = await fetch(`${API_URL}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...order,
          designNotes: notesToSave
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update order');
      }

      setSnackbar({
        open: true,
        message: 'Artwork and notes saved successfully!',
        severity: 'success'
      });

      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/app/sales-workflow?tab=2'); // Navigate to Order Tracking tab
      }, 1500);

    } catch (error) {
      console.error('Error saving:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save changes',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/sales-workflow?tab=2'); // Navigate back to Order Tracking tab
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
        <Alert severity="error">Order not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleCancel}
          sx={{ mt: 2 }}
        >
          Back to Order Tracking
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
      <PageHeader 
        title={`Attach Artwork - Order #${orderId}`}
        subtitle={order.businessName || 'Unknown Business'}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {/* Artwork Upload Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AttachFileIcon color="primary" />
                <Typography variant="h6">
                  Upload Artwork Files
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload artwork files for this order. Files will be saved immediately.
              </Typography>
            </Box>
            
            <OrderAttachments
              orderId={orderId}
              attachments={savedAttachments}
              onAttachmentsChange={setSavedAttachments}
              tempAttachments={tempAttachments}
              onTempAttachmentsChange={setTempAttachments}
              type="order"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Design Notes Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Design Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add notes about the artwork or design requirements.
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={6}
              value={designNotes}
              onChange={(e) => setDesignNotes(e.target.value)}
              placeholder="Enter design notes here..."
              variant="outlined"
            />

            {order.designNotes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Previous Design Notes:
                </Typography>
                <Stack spacing={1.5}>
                  {parseNotesBlob(order.designNotes, { idPrefix: 'design-note', noteType: 'design' }).map((note) => (
                    <NoteCard key={note.id} note={note} color="secondary" bgColor="purple.50" borderColor="purple.200" />
                  ))}
                </Stack>
              </Box>
            )}
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save & Return'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Container>
  );
};

export default AttachArtwork;

